/**
 * Utility functions for including branch context in API requests
 */

import { BranchType } from '../interfaces/delivery-company/branches';

/**
 * Creates API configuration with branch context header
 * @param config - Base axios configuration (from AuthContext)
 * @param selectedBranch - Currently selected branch (from SelectedBranchContext)
 * @returns Configuration object with branch header added
 */
export function getConfigWithBranch(
  config: { headers?: Record<string, string> },
  selectedBranch: BranchType | null
): { headers?: Record<string, string>; params?: Record<string, string> } {
  if (!selectedBranch) {
    return config;
  }

  return {
    ...config,
    params: {
      ...(config as any).params,
      context_branch_id: selectedBranch.id.toString(),
    },
  };
}

/**
 * Validates that all items have the same branch as the selected branch
 * @param items - Array of items with branch property
 * @param selectedBranch - Currently selected branch
 * @param itemType - Type of item (for error message)
 * @returns Object with isValid boolean and error message if invalid
 */
export function validateBranchConsistency<T extends { branch?: BranchType | { id: number; name: string } | null }>(
  items: T[],
  selectedBranch: BranchType | null,
  itemType = 'item'
): { isValid: boolean; error?: string; invalidItems?: T[] } {
  if (!selectedBranch) {
    return { isValid: true };
  }

  const invalidItems = items.filter((item) => {
    if (!item.branch) return false; // Allow items without branch
    
    const itemBranchId = typeof item.branch === 'object' ? item.branch.id : item.branch;
    return itemBranchId !== selectedBranch.id;
  });

  if (invalidItems.length > 0) {
    const branchNames = Array.from(
      new Set(
        invalidItems.map((item) => {
          if (!item.branch) return 'Unknown';
          return typeof item.branch === 'object' ? item.branch.name : 'Unknown';
        })
      )
    ).join(', ');

    return {
      isValid: false,
      error: `Cannot proceed: Some ${itemType}s belong to different branches (${branchNames}). You are currently working in branch "${selectedBranch.name}". Please switch to the correct branch or deselect items from other branches.`,
      invalidItems,
    };
  }

  return { isValid: true };
}

/**
 * Filters items to only include those from the selected branch
 * @param items - Array of items with branch property
 * @param selectedBranch - Currently selected branch
 * @returns Filtered array containing only items from the selected branch
 */
export function filterByBranch<T extends { branch?: BranchType | { id: number; name: string } | null }>(
  items: T[],
  selectedBranch: BranchType | null
): T[] {
  if (!selectedBranch) {
    return items;
  }

  return items.filter((item) => {
    if (!item.branch) return true; // Include items without branch
    
    const itemBranchId = typeof item.branch === 'object' ? item.branch.id : item.branch;
    return itemBranchId === selectedBranch.id;
  });
}

/**
 * Checks if an item belongs to the selected branch
 * @param item - Item with branch property
 * @param selectedBranch - Currently selected branch
 * @returns True if item belongs to selected branch or has no branch
 */
export function isItemInSelectedBranch<T extends { branch?: BranchType | { id: number; name: string } | null }>(
  item: T,
  selectedBranch: BranchType | null
): boolean {
  if (!selectedBranch) {
    return true;
  }

  if (!item.branch) {
    return true; // Allow items without branch
  }

  const itemBranchId = typeof item.branch === 'object' ? item.branch.id : item.branch;
  return itemBranchId === selectedBranch.id;
}


